'use client';

import { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import finalLogo from '../assets/images/final-logo.svg';
import signUpBg from '../assets/images/signup.webp';
import Button from '../components/shared/Button';
import CustomCheckbox from '../components/shared/CustomCheckbox';
import { UserService } from '../services';
import EmailIcon from '../assets/icons/emailSentSuccess.svg';
import Modal from '../components/modals/modal';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Toast } from '../components/shared/Toast';
import {
	isValidNameCheck,
	isValidEmailCheck,
	isValidPasswordCheck,
	isValidNameLength,
	isValidPasswordLength,
	doPasswordsMatch
} from '../utils/validators';

const styles = {
	section: 'flex flex-col lg:flex-row min-h-screen bg-bgColor overflow-x-hidden',
	leftColumn:
		'w-full lg:w-1/2 py-5 px-6 xl:pr-[243px] xl:pl-[150px]',
	imageStyle: 'h-12 w-[260px] mb-20 mx-auto lg:mx-0',
	mainHeading: 'text-left text-main leading-39 font-semibold text-dark-blue mb-3',
	subHeading: 'text-left text-lg leading-100 text-gray mb-[50px] font-medium',
	label: 'block text-base leading-100 font-medium text-dark-blue mb-3',
	input: 'w-full pl-3.5 py-3.5 border-1p3 border-lightGrey rounded-10p placeholder-gray-400',
	rightBgImage: 'hidden lg:block fixed top-0 right-0 w-1/2 h-screen bg-cover bg-center',
	carouselText: 'text-carousel leading-130 font-semibold text-white'
};

const SignupPage = () => {
	const location = useLocation();
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [agreeToTerms, setAgreeToTerms] = useState(false);
	const [currentTextIndex, setCurrentTextIndex] = useState(0);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [showEmailSentModal, setShowEmailSentModal] = useState(false);
	const [modalDescription, setModalDescription] = useState('');
	const navigate = useNavigate();

	const defaultPlaceholders = {
		firstName: 'First Name *',
		lastName: 'Last Name *',
		email: 'Email Address *',
	};
	const [phFirstName] = useState(defaultPlaceholders.firstName);
	const [phLastName] = useState(defaultPlaceholders.lastName);
	const [phEmail, setPhEmail] = useState(defaultPlaceholders.email);

	const isValidEmail = (value) => isValidEmailCheck(value);
	const isValidFirstName = (value) => isValidNameCheck(value);
	const isValidLastName = (value) => isValidNameCheck(value);
	const isValidPassword = (value) => isValidPasswordCheck(value);

	const carouselTexts = [
		'Turn your ideas into professionally formatted documents with ease — WriteForm handles the structure, so you can focus on what matters most: your content.',
		'Streamline your writing process with intuitive tools designed for efficiency and clarity.',
		'Save time and reduce stress — WriteForm delivers accurate, consistent formatting so your papers are submission ready.',
		'Access your documents anywhere, on any device — securely stored in the cloud.'
	];

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentTextIndex((prevIndex) => (prevIndex + 1) % carouselTexts.length);
		}, 5000);

		return () => clearInterval(interval);
	}, [carouselTexts.length]);

	// Clear error when agreeToTerms changes
	

	useEffect(() => {
		const img = new Image();
		img.src = signUpBg;
		img.onload = () => setIsImageLoaded(true);
	}, []);

	// Prefill fields when coming back from verify page
	useEffect(() => {
		if (location && location.state) {
			if (location.state.firstName) setFirstName(location.state.firstName);
			if (location.state.lastName) setLastName(location.state.lastName);
			if (location.state.email) setEmail(location.state.email);
		}
	}, [location]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setIsLoading(true);

		// First name validation
		if (!firstName.trim()) {
			Toast.error('Please enter your first name.');
			setIsLoading(false);
			return;
		}
		if (!isValidNameLength(firstName)) {
			Toast.error('Minimum length for first name is 3 characters.');
			setIsLoading(false);
			return;
		}
		if (!isValidFirstName(firstName.trim())) {
			Toast.error('First name can only include letters.');
			setIsLoading(false);
			return;
		}

		// Last name validation
		if (!lastName.trim()) {
			Toast.error('Please enter your last name.');
			setIsLoading(false);
			return;
		}
		if (!isValidNameLength(lastName)) {
			Toast.error('Minimum length for last name is 3 characters.');
			setIsLoading(false);
			return;
		}
		if (!isValidLastName(lastName.trim())) {
			Toast.error('Last name can only include letters.');
			setIsLoading(false);
			return;
		}

		// Email validation
		const trimmedEmail = email.trim();
		if (!trimmedEmail) {
			Toast.error('Please enter your email address.');
			setIsLoading(false);
			return;
		}
		if (!isValidEmail(trimmedEmail)) {
			Toast.error('Please enter a valid email address (e.g.,name@email.com).');
			setIsLoading(false);
			return;
		}

		// Password validation
		if (!password.trim()) {
			Toast.error('Please create a password.');
			setIsLoading(false);
			return;
		}
		if (!isValidPasswordLength(password)) {
			Toast.error('Password must be at least 8 characters.');
			setIsLoading(false);
			return;
		}
		if (!isValidPassword(password)) {
			Toast.error('Use a mix of letters, numbers, and symbols for a stronger password.');
			setIsLoading(false);
			return;
		}

		// Confirm password validation
		if (!confirmPassword.trim()) {
			Toast.error('Please confirm your password.');
			setIsLoading(false);
			return;
		}
		if (!doPasswordsMatch(password, confirmPassword)) {
			Toast.error("Passwords don't match. Try again.");
			setIsLoading(false);
			return;
		}

		if (!agreeToTerms) {
			Toast.error('Please accept terms and conditions to continue.');
			setIsLoading(false);
			return;
		}

		if (agreeToTerms) {
			Toast.success("Thanks! You've agreed to the Terms & Conditions.");
		}
		

		try {
			const apiPromise = UserService.register(firstName, lastName, trimmedEmail, password);
			const delayPromise = new Promise((resolve) => setTimeout(resolve, 5000));
			const response = await Promise.all([apiPromise, delayPromise]).then(([res]) => res);

			if (response.status === 200) {
				setModalDescription(response.message);
				setShowEmailSentModal(true);
			} else if (response.status === 401) {
				// Invalid credentials
				const message = response.message;
				// Toast.error(message);
				Toast.error(message);
			} else {
				// Other errors
				const message = response.message || 'Registration failed. Please try again.';
				// Toast.error(message);
				Toast.error(message);
				console.error('Registration failed:', response);
			}
		} catch (error) {
			console.error('Registration error:', error);
			// Toast.error('Network error. Please check your connection and try again.');
			Toast.error('Network error. Please check your connection and try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const navigateToCodeScreen = () => {
		// Pass the email and names to the /verify-email page using state
		navigate('/verify-email', { state: { email, firstName, lastName, mode: 'signup' } });
	};


	return (
		<div className={styles.section}>
			{/* Left Column (Signup Form) */}
			<div className={styles.leftColumn}>
				<div className="w-full max-w-md lg:w-[520px] lg:h-[645px]">
					<div>
						<img
							className={styles.imageStyle}
							src={finalLogo || '/placeholder.svg'}
							alt="WriteForm Logo"
						/>
						<h2 className={styles.mainHeading}>
							<span className="[font-family:'Plus Jakarta Sans',sans-serif]">Get started with WriteForm</span>
						</h2>
						<p className={styles.subHeading}>
							Fill in your details below to create your account and start writing with confidence.
						</p>
					</div>
					<form className="" onSubmit={handleSubmit}>
						{error && (
							<div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
								{error}
							</div>
						)}
						<div className="">
							<div className="flex gap-4 mb-[30px]">
								<div className="flex-1">
									<label htmlFor="first-name" className={styles.label}>
										First Name <span className="text-red-500">*</span>
									</label>
									<input
										id="first-name"
										name="firstName"
										type="text"
										required
										className={styles.input}
										placeholder={phFirstName}
										value={firstName}
										onChange={(e) => {
											const value = e.target.value;
											setFirstName(value);
											if (error && isValidFirstName(value.trim())) {
												setError('');
											}
										}}
										disabled={isLoading}
									/>
								</div>
								<div className="flex-1">
									<label htmlFor="last-name" className={styles.label}>
										Last Name <span className="text-red-500">*</span>
									</label>
									<input
										id="last-name"
										name="lastName"
										type="text"
										required
										className={styles.input}
										placeholder={phLastName}
										value={lastName}
										onChange={(e) => {
											const value = e.target.value;
											setLastName(value);
											if (error && isValidLastName(value.trim())) {
												setError('');
											}
										}}
										disabled={isLoading}
									/>
								</div>
							</div>

							<div className="mb-[30px]">
								<label htmlFor="email-address" className={styles.label}>
									Email Address <span className="text-red-500">*</span>
								</label>
								<input
									id="email-address"
									name="email"
									type="email"
									autoComplete="email"
									required
									className={styles.input}
									placeholder={phEmail}
									value={email}
									onChange={(e) => {
										const value = e.target.value;
										setEmail(value);
										if (error && isValidEmail(value.trim())) {
											setError('');
										}
									}}
									disabled={isLoading}
									onMouseEnter={() => setPhEmail('Email Address *')}
									onMouseLeave={() => setPhEmail(defaultPlaceholders.email)}
								/>
							</div>

							<div className="mb-[30px]">
								<label htmlFor="password" className={styles.label}>
									Password <span className="text-red-500">*</span>
								</label>
								<div className="relative">
									<input
										id="password"
										name="password"
										type={showPassword ? 'text' : 'password'}
										autoComplete="new-password"
										required
										className={styles.input}
										placeholder="••••••••••••"
										value={password}
										onChange={(e) => {
											const value = e.target.value;
											setPassword(value);
											if (error && isValidPassword(value.trim())) {
												setError('');
											}
										}}
										disabled={isLoading}
										minLength={8}
									/>
									<div
										className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
										onClick={() => setShowPassword((prev) => !prev)}
									>
										{showPassword ? (
											// Eye Open
											<svg
												width="22"
												height="22"
												viewBox="0 0 22 22"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<g opacity="0.5">
													<path
														d="M10.9948 5.17676C14.577 5.17684 17.6093 7.53095 18.6285 10.7764C17.6094 14.0219 14.5771 16.3769 10.9948 16.377C7.41235 16.377 4.38016 14.022 3.36096 10.7764C4.38027 7.53095 7.41248 5.17676 10.9948 5.17676ZM10.9948 7.57715C10.1462 7.57715 9.33214 7.91374 8.73206 8.51367C8.13203 9.1137 7.79466 9.92782 7.79456 10.7764C7.79456 11.625 8.132 12.439 8.73206 13.0391C9.33217 13.6392 10.1461 13.9766 10.9948 13.9766C11.8434 13.9765 12.6574 13.6391 13.2574 13.0391C13.8574 12.439 14.1949 11.6249 14.1949 10.7764C14.1948 9.92782 13.8575 9.1137 13.2574 8.51367C12.6574 7.91366 11.8433 7.5772 10.9948 7.57715ZM10.9948 9.17676C11.419 9.17678 11.8256 9.34556 12.1256 9.64551C12.4256 9.94547 12.5943 10.3522 12.5944 10.7764C12.5944 11.2007 12.4257 11.6081 12.1256 11.9082C11.8256 12.2081 11.4189 12.3769 10.9948 12.377C10.5704 12.377 10.163 12.2083 9.86292 11.9082C9.56295 11.6082 9.39514 11.2006 9.39514 10.7764C9.39525 10.3523 9.5631 9.94545 9.86292 9.64551C10.163 9.34545 10.5704 9.17676 10.9948 9.17676Z"
														fill="#180A63"
													/>
												</g>
											</svg>
										) : (
											// Eye Closed
											<svg
												width="22"
												height="22"
												viewBox="0 0 22 22"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<g opacity="0.5">
													<path
														d="M18.2783 17.1426L17.2178 18.2031L3.0752 4.06055L4.13574 3L18.2783 17.1426ZM7.54785 9.5332C7.47307 9.80619 7.43363 10.09 7.43359 10.3779C7.43359 11.2266 7.77104 12.0405 8.37109 12.6406C8.97121 13.2407 9.7851 13.5781 10.6338 13.5781C10.9215 13.5781 11.2048 13.5376 11.4775 13.4629L13.4736 15.459C12.591 15.7943 11.634 15.9785 10.6338 15.9785C7.05139 15.9785 4.0192 13.6235 3 10.3779C3.40221 9.09733 4.11847 7.95616 5.05762 7.04297L7.54785 9.5332ZM10.6338 4.77832C14.216 4.7784 17.2483 7.13251 18.2676 10.3779C17.8481 11.7139 17.0866 12.898 16.0869 13.8301L13.6631 11.4062C13.7746 11.078 13.834 10.7311 13.834 10.3779C13.8339 9.52938 13.4965 8.71526 12.8965 8.11523C12.2964 7.51523 11.4824 7.17876 10.6338 7.17871C10.2807 7.17871 9.93369 7.23735 9.60547 7.34863L7.62305 5.36621C8.55258 4.9883 9.5685 4.77832 10.6338 4.77832ZM9.66309 11.6484C9.60698 11.6056 9.55242 11.5602 9.50195 11.5098C9.45149 11.4593 9.40609 11.4048 9.36328 11.3486L9.66309 11.6484ZM11.1074 8.85059C11.3532 8.9269 11.5793 9.06177 11.7646 9.24707C11.9497 9.43209 12.0838 9.65802 12.1602 9.90332L11.1074 8.85059Z"
														fill="#180A63"
													/>
												</g>
											</svg>
										)}
									</div>
								</div>
								<p className="text-[12px] text-gray mt-2">
									Use at least 8 characters with letters, numbers, and symbols.
								</p>
							</div>

							<div className="mb-3">
								<label htmlFor="confirm-password" className={styles.label}>
									Confirm Password <span className="text-red-500">*</span>
								</label>
								<div className="relative">
									<input
										id="confirm-password"
										name="confirmPassword"
										type={showConfirmPassword ? 'text' : 'password'}
										autoComplete="new-password"
										required
										className={styles.input}
										placeholder="••••••••••••"
										value={confirmPassword}
										onChange={(e) => {
											const value = e.target.value;
											setConfirmPassword(value);
											if (error && isValidPassword(value.trim())) {
												setError('');
											}
										}}
										disabled={isLoading}
										minLength={8}
									/>
									<div
										className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
										onClick={() => setShowConfirmPassword((prev) => !prev)}
									>
										{showConfirmPassword ? (
											// Eye Open
											<svg
												width="22"
												height="22"
												viewBox="0 0 22 22"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<g opacity="0.5">
													<path
														d="M10.9948 5.17676C14.577 5.17684 17.6093 7.53095 18.6285 10.7764C17.6094 14.0219 14.5771 16.3769 10.9948 16.377C7.41235 16.377 4.38016 14.022 3.36096 10.7764C4.38027 7.53095 7.41248 5.17676 10.9948 5.17676ZM10.9948 7.57715C10.1462 7.57715 9.33214 7.91374 8.73206 8.51367C8.13203 9.1137 7.79466 9.92782 7.79456 10.7764C7.79456 11.625 8.132 12.439 8.73206 13.0391C9.33217 13.6392 10.1461 13.9766 10.9948 13.9766C11.8434 13.9765 12.6574 13.6391 13.2574 13.0391C13.8574 12.439 14.1949 11.6249 14.1949 10.7764C14.1948 9.92782 13.8575 9.1137 13.2574 8.51367C12.6574 7.91366 11.8433 7.5772 10.9948 7.57715ZM10.9948 9.17676C11.419 9.17678 11.8256 9.34556 12.1256 9.64551C12.4256 9.94547 12.5943 10.3522 12.5944 10.7764C12.5944 11.2007 12.4257 11.6081 12.1256 11.9082C11.8256 12.2081 11.4189 12.3769 10.9948 12.377C10.5704 12.377 10.163 12.2083 9.86292 11.9082C9.56295 11.6082 9.39514 11.2006 9.39514 10.7764C9.39525 10.3523 9.5631 9.94545 9.86292 9.64551C10.163 9.34545 10.5704 9.17676 10.9948 9.17676Z"
														fill="#180A63"
													/>
												</g>
											</svg>
										) : (
											// Eye Closed
											<svg
												width="22"
												height="22"
												viewBox="0 0 22 22"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<g opacity="0.5">
													<path
														d="M18.2783 17.1426L17.2178 18.2031L3.0752 4.06055L4.13574 3L18.2783 17.1426ZM7.54785 9.5332C7.47307 9.80619 7.43363 10.09 7.43359 10.3779C7.43359 11.2266 7.77104 12.0405 8.37109 12.6406C8.97121 13.2407 9.7851 13.5781 10.6338 13.5781C10.9215 13.5781 11.2048 13.5376 11.4775 13.4629L13.4736 15.459C12.591 15.7943 11.634 15.9785 10.6338 15.9785C7.05139 15.9785 4.0192 13.6235 3 10.3779C3.40221 9.09733 4.11847 7.95616 5.05762 7.04297L7.54785 9.5332ZM10.6338 4.77832C14.216 4.7784 17.2483 7.13251 18.2676 10.3779C17.8481 11.7139 17.0866 12.898 16.0869 13.8301L13.6631 11.4062C13.7746 11.078 13.834 10.7311 13.834 10.3779C13.8339 9.52938 13.4965 8.71526 12.8965 8.11523C12.2964 7.51523 11.4824 7.17876 10.6338 7.17871C10.2807 7.17871 9.93369 7.23735 9.60547 7.34863L7.62305 5.36621C8.55258 4.9883 9.5685 4.77832 10.6338 4.77832ZM9.66309 11.6484C9.60698 11.6056 9.55242 11.5602 9.50195 11.5098C9.45149 11.4593 9.40609 11.4048 9.36328 11.3486L9.66309 11.6484ZM11.1074 8.85059C11.3532 8.9269 11.5793 9.06177 11.7646 9.24707C11.9497 9.43209 12.0838 9.65802 12.1602 9.90332L11.1074 8.85059Z"
														fill="#180A63"
													/>
												</g>
											</svg>
										)}
									</div>
								</div>
							</div>
						</div>

						<div className="flex items-center mb-[60px]">
							<div className="flex items-center">
								<CustomCheckbox
									id="agree-terms"
									checked={agreeToTerms}
									onChange={(e) => setAgreeToTerms(e.target.checked)}
									// The default accent color is B67FE2, else pass accentColor="#0F9AFB"
								/>
								<label
									htmlFor="agree-terms"
									className="ml-[6px] block text-[16px] leading-[21px] text-gray font-medium"
								>
									I agree to the{' '}
									<a href="/terms" className="text-dark-blue underline">
										Terms & Conditions
									</a>{' '}
									and{' '}
									<a href="/privacy-policy" className="text-dark-blue underline">
										Privacy Policy
									</a>
									<span className="text-red-500"> *</span>
								</label>
							</div>
						</div>

						<div>
							<Button
								type="submit"
								onClick={handleSubmit}
								disabled={isLoading}
								className="disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isLoading ? "Signing up..." : "Sign up"}
							</Button>
						</div>

						<div className="text-left text-[16px] leading-[21px] font-medium text-gray mt-5 pb-5">
							Already have an account?{' '}
							<a href="/login" className="text-dark-blue underline">
								Login
							</a>
						</div>
					</form>
				</div>
			</div>

			{/* Right Column (Image and Text) */}
			<div
				className={styles.rightBgImage}
				style={isImageLoaded ? { backgroundImage: `url(${signUpBg})` } : {}}
			>
				{!isImageLoaded && (
					<div className="absolute inset-0">
						<Skeleton
							height="100%"
							width="100%"
							baseColor="#E5E7EB"
							highlightColor="#F3F4F6"
							duration={1.2}
						/>
					</div>
				)}
				<div className="absolute inset-0 bg-opacity-40 flex flex-col justify-end p-8">
					<div className="text-white bg-white/5 rounded-lg p-6 mx-4 backdrop-blur-30 border-2 border-white/10">
						<div className="flex justify-start space-x-2 mb-4">
							{carouselTexts.map((_, index) => (
								<span
									key={index}
									className={`transition-all duration-300 ${
										index === currentTextIndex
											? 'h-2 w-8 bg-white rounded-full'
											: 'h-2 w-2 bg-white opacity-50 rounded-full'
									}`}
								></span>
							))}
						</div>
						<p className={styles.carouselText}>{carouselTexts[currentTextIndex]}</p>
					</div>
				</div>
			</div>
			<Modal
				icon={<img src={EmailIcon} alt="Email Sent" className="mx-auto" />}
				isOpen={showEmailSentModal}
				heading={'Email sent successfully'}
				description={modalDescription}
				buttonText={'Got it'}
				onButtonClick={navigateToCodeScreen}
			/>
		</div>
	);
};

export default SignupPage;
